import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { Pencil, Trash2, Send, Eye, Check, X, Download } from 'lucide-react';
import Header from './Header';
import useAuth from './useAuth';
import { BACKEND_URL } from './backendConfig';
import useEscapeClose from './useEscapeClose';
import ModuleHelp from './ModuleHelp';
import './NFCPortal.css';
import { fmtDateTimeVN } from './timeZone';

const API = `${BACKEND_URL}/api/production/nfc`;

const STATUS_CLASS = {
  DRAFT: 'st-draft', SUBMITTED: 'st-submitted', PENDING: 'st-submitted',
  APPROVED: 'st-approved', REJECTED: 'st-rejected',
};
// Display labels match the legacy badge(): DRAFT → NOT SUBMITTED, SUBMITTED → WAITING APPROVAL.
// These are i18n keys, not text — the value stored and filtered on is still DRAFT / SUBMITTED / ...
const STATUS_LABEL = {
  DRAFT: 'nfc.stDraft', SUBMITTED: 'nfc.stWaiting', PENDING: 'nfc.stWaiting',
  APPROVED: 'nfc.stApproved', REJECTED: 'nfc.stRejected',
};

/* THE GUIDE (Max 01/11: every module has one). Coordinator and manager sections show by role and by function grant, so a
   step for a part the person does not get is skipped by TourGuide. */
const TOUR = [
  { selector: '.nfc-who', title: 'tourNfc.roleT', body: 'tourNfc.roleB' },
  { selector: '.nfc-type-toggle', title: 'tourNfc.typeT', body: 'tourNfc.typeB' },
  { selector: '.nfc-import-box', title: 'tourNfc.importT', body: 'tourNfc.importB' },
  { selector: '.nfc-form-actions', title: 'tourNfc.saveT', body: 'tourNfc.saveB' },
  { selector: '[data-tour="nfc-mine"]', title: 'tourNfc.mineT', body: 'tourNfc.mineB' },
  { selector: '[data-tour="nfc-review"]', title: 'tourNfc.reviewT', body: 'tourNfc.reviewB' },
];

const emptyForm = () => ({
  requestId: '', requestType: 'ISSUANCE', labelType: 'NFC',
  projectName: '', batchName: '', qty: '', code: '', style: '', color: '', vendor: '',
  notes: '', importItemsText: '',
});

// Parse the import textarea/CSV into rows (matches backend nfcParseImportItems + legacy parseImportItems_).
const parseImportRows = (text) => String(text || '').split(/\r?\n/).map((l) => l.trim()).filter((l) => l && l[0] !== '#').map((line) => {
  const p = line.split(/\t|,/).map((s) => s.trim());
  // PROJECT, BATCH, QTY, CODE, STYLE, COLOR, VENDOR — Destination was dropped on 2026-08-05.
  return { projectName: p[0] || '', batchName: p[1] || '', qty: p[2] || '', code: p[3] || '', style: p[4] || '', color: p[5] || '', vendor: p[6] || '', ok: !!p[0] && !!p[1] && Number(p[2] || 0) > 0 };
});
const serializeImportRows = (rows) => rows.map((r) => [r.projectName, r.batchName, r.qty, r.code, r.style, r.color, r.vendor].join(',')).join('\n');

// Replacement rows: OLD_TOKEN, REASON, NOTES (mirrors backend nfcParseReplacementItems + legacy parseReplacementItemsText).
const parseReplacementRows = (text) => String(text || '').split(/\r?\n/).map((l) => l.trim()).filter((l) => l && l[0] !== '#')
  .map((line) => { const p = line.split(/\t|,/).map((s) => s.trim()); return { oldToken: p[0] || '', reason: p[1] || 'NFC chip defective', notes: p[2] || '' }; })
  .filter((it) => it.oldToken || it.notes);
const serializeReplacementRows = (rows) => rows.map((r) => [r.oldToken, r.reason, r.notes].join(',')).join('\n');
const REPL_STATUS_LABEL = { OK: 'nfc.replOk', NOT_FOUND: 'nfc.replNotFound', REPLACED: 'nfc.replReplaced', DUPLICATE: 'nfc.replDuplicate' };

const fmtDate = (iso) => fmtDateTimeVN(iso);

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const s = String(status || '').toUpperCase();
  if (!s) return <span>—</span>;
  // Unknown statuses fall back to the raw value rather than a missing-key string.
  return <span className={`nfc-badge ${STATUS_CLASS[s] || 'st-draft'}`}>{STATUS_LABEL[s] ? t(STATUS_LABEL[s]) : s}</span>;
}

function NFCPortal() {
  const { t } = useTranslation();
  // RBAC section permissions (layered on top of the coordinator/manager workflow roles).
  const { permissions: rbac } = useAuth();
  const canSection = (key) => !!(rbac && rbac[key]);
  const canEditSection = (key) => !!(rbac && rbac[key] === 'edit');
  const [ctx, setCtx] = useState({ email: '', roles: [], role: null });
  const [myRequests, setMyRequests] = useState([]);
  const [managerRequests, setManagerRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [replacementCheck, setReplacementCheck] = useState(null);
  const [validatingRepl, setValidatingRepl] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);

  // Esc closes whichever of these is on top. The generation-progress dialog is deliberately absent:
  // it has no close button either, because the job behind it keeps running.
  useEscapeClose(() => setDetail(null), !!detail && !saving);
  useEscapeClose(() => setRejectFor(null), !!rejectFor);
  useEscapeClose(() => setDeleteFor(null), !!deleteFor);
  const [approveProg, setApproveProg] = useState(null);
  const approvePollRef = useRef(null);
  const [accessList, setAccessList] = useState([]);
  const [knownUsers, setKnownUsers] = useState([]);
  const [newAccessEmail, setNewAccessEmail] = useState('');
  const [newAccessRole, setNewAccessRole] = useState('coordinator');
  const [accessBusy, setAccessBusy] = useState(false);

  const flash = (msg, ok = true) => { if (ok) { setMessage(msg); setError(''); } else { setError(msg); setMessage(''); } };

  // Auto-dismiss the notification banner after 25s (resets whenever a new message/error appears).
  useEffect(() => {
    if (!message && !error) return undefined;
    const timer = setTimeout(() => { setMessage(''); setError(''); }, 25000);
    return () => clearTimeout(timer);
  }, [message, error]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bootstrap`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || i18n.t('xNfc.loadFailed'));
      // Keep the manager/coordinator view the user picked across reloads (don't snap back to the server default).
      setCtx((prev) => ({ email: data.email, roles: data.roles || [], role: prev.role || data.role }));
      setMyRequests(data.myRequests || []);
      setManagerRequests(data.managerRequests || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAccess = useCallback(async () => {
    try {
      const res = await fetch(`${API}/access`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setAccessList(data.access || []);
      setKnownUsers(data.knownUsers || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Load the access list whenever the Manager view is active.
  useEffect(() => { if (ctx.role === 'manager' && ctx.roles.includes('manager')) loadAccess(); }, [ctx.role, ctx.roles, loadAccess]);

  const setField = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const resetForm = () => { setForm(emptyForm()); setReplacementCheck(null); setImportFileName(''); };
  const cancelForm = () => { resetForm(); flash(t('nfc.flashCleared')); };

  // Issuance import: mirror the first item into the top fields + set QTY to the total, so the request-level
  // Project/Batch aren't empty on save (ports legacy syncEditableImportPreview).
  const applyImportRows = (rows) => {
    const first = rows[0] || {};
    const total = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
    setForm((prev) => ({
      ...prev,
      importItemsText: serializeImportRows(rows),
      projectName: first.projectName || '',
      batchName: first.batchName || '',
      qty: total ? String(total) : '',
      style: first.style || '',
      color: first.color || '',
      vendor: first.vendor || '',
      code: first.code || '',
    }));
  };

  const isReplacement = form.requestType === 'REPLACEMENT';

  const handleSave = async (submitAfter) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/requests`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xNfc.saveFailed'));
      if (submitAfter) {
        const r2 = await fetch(`${API}/requests/${encodeURIComponent(data.requestId)}/submit`, { method: 'POST', credentials: 'include' });
        const d2 = await r2.json();
        if (!r2.ok) throw new Error(d2.error || t('xNfc.submitFailed'));
        flash(t('nfc.flashSubmitted', { id: data.requestId }));
      } else {
        flash(t('nfc.flashDraft', { id: data.requestId }));
      }
      resetForm();
      await load();
    } catch (err) {
      flash(err.message, false);
    } finally {
      setSaving(false);
    }
  };

  // Auto-validate defective tokens against the ledger (debounced), mirroring legacy validateReplacementPreview.
  useEffect(() => {
    if (form.requestType !== 'REPLACEMENT') { setValidatingRepl(false); return undefined; }
    const text = form.importItemsText;
    if (!parseReplacementRows(text).length) { setReplacementCheck(null); setValidatingRepl(false); return undefined; }
    setValidatingRepl(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/requests/validate-replacement`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ importItemsText: text }),
        });
        const data = await res.json();
        if (res.ok) setReplacementCheck(data);
        else setReplacementCheck({ items: [], warnings: [i18n.t('xNfc.validateFailed', { error: data.error || i18n.t('xNfc.errorWord') })] });
      } catch (err) {
        setReplacementCheck({ items: [], warnings: [i18n.t('xNfc.validateNow', { error: err.message })] });
      } finally { setValidatingRepl(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.importItemsText, form.requestType]);

  const editRequest = (r) => {
    setForm({
      requestId: r.requestId,
      requestType: r.requestType || 'ISSUANCE',
      labelType: r.labelType || 'NFC',
      projectName: r.projectName || '', batchName: r.batchName || '', qty: r.qty || '',
      code: r.code || '', style: r.style || '', color: r.color || '', vendor: r.vendor || '',
      notes: r.notes || '',
      importItemsText: (r.importItems || []).map((it) => (
        r.requestType === 'REPLACEMENT'
          ? [it.oldNfcUrl || it.oldToken, it.reason, it.notes].filter(Boolean).join(', ')
          : [it.projectName, it.batchName, it.qty, it.code, it.style, it.color, it.vendor].join(',')
      )).join('\n'),
    });
    setImportFileName((r.importItems || []).length ? t('nfc.progSavedImport') : '');
    setReplacementCheck(null);
    flash(t('nfc.flashLoadedEdit', { id: r.requestId }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitExisting = async (requestId) => {
    try {
      const res = await fetch(`${API}/requests/${encodeURIComponent(requestId)}/submit`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xNfc.submitFailed'));
      flash(t('nfc.flashSubmitted', { id: requestId }));
      await load();
    } catch (err) { flash(err.message, false); }
  };

  const doDeleteRequest = async () => {
    const requestId = deleteFor;
    setDeleteFor(null);
    try {
      const res = await fetch(`${API}/requests/${encodeURIComponent(requestId)}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xNfc.deleteFailed'));
      flash(t('nfc.flashDeleted', { id: requestId }));
      await load();
    } catch (err) { flash(err.message, false); }
  };

  const stopApprovePoll = () => {
    if (approvePollRef.current) { clearInterval(approvePollRef.current); approvePollRef.current = null; }
  };

  const approve = async (requestId) => {
    // Guard: while an approval is running, ignore further clicks so tokens can't be double-generated.
    if (approveProg && approveProg.status === 'running') return;
    const row = managerRequests.find((r) => r.requestId === requestId);
    const total = Number(row?.qty) || 0;
    setApproveProg({ requestId, status: 'running', phase: t('nfc.genTitle'), done: 0, total, message: t('nfc.progStarting') });

    // Poll the backend for live generation progress while the POST is in flight.
    stopApprovePoll();
    approvePollRef.current = setInterval(async () => {
      try {
        const pr = await fetch(`${API}/requests/${encodeURIComponent(requestId)}/approve-progress`, { credentials: 'include' });
        if (!pr.ok) return;
        const p = await pr.json();
        if (p && p.status && p.status !== 'unknown') {
          setApproveProg((cur) => (cur && cur.requestId === requestId && cur.status !== 'done' ? { ...cur, ...p } : cur));
        }
      } catch { /* transient poll error — ignore */ }
    }, 500);

    try {
      const res = await fetch(`${API}/requests/${encodeURIComponent(requestId)}/approve`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      stopApprovePoll();
      if (!res.ok) throw new Error(data.error || t('xNfc.approveFailed'));
      setApproveProg({ requestId, status: 'done', phase: t('nfc.progCompleted'), done: data.qty, total: data.qty, message: t('nfc.progGenerated', { qty: data.qty }) });
      flash(t('nfc.flashApproved', { id: requestId, qty: data.qty }));
      await load();
    } catch (err) {
      stopApprovePoll();
      setApproveProg((cur) => (cur && cur.requestId === requestId ? { ...cur, status: 'error', message: err.message } : cur));
    }
  };

  useEffect(() => () => stopApprovePoll(), []);

  // ---- Manage portal access (function_access) ----
  const toggleAccess = async (email, role, enable) => {
    setAccessBusy(true);
    try {
      const res = await fetch(`${API}/access`, {
        method: enable ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email, role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xNfc.updateFailed'));
      flash(t(enable ? 'nfc.flashGranted' : 'nfc.flashRevoked', { role, email }));
      await loadAccess();
    } catch (err) { flash(err.message, false); }
    finally { setAccessBusy(false); }
  };

  const addAccess = async () => {
    const email = newAccessEmail.trim().toLowerCase();
    if (!email) { flash(t('nfc.flashEmailReq'), false); return; }
    await toggleAccess(email, newAccessRole, true);
    setNewAccessEmail('');
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { setRejectError(true); return; }
    try {
      const res = await fetch(`${API}/requests/${encodeURIComponent(rejectFor)}/reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ reason: rejectReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xNfc.rejectFailed'));
      flash(t('nfc.flashRejected', { id: rejectFor }));
      setRejectFor(null); setRejectReason(''); setRejectError(false);
      await load();
    } catch (err) { flash(err.message, false); }
  };

  // Downloads the order file with the auth cookie attached (fetch → blob).
  const downloadFile = async (requestId) => {
    try {
      const res = await fetch(`${API}/requests/${encodeURIComponent(requestId)}/download`, { credentials: 'include' });
      if (!res.ok) throw new Error(t('xNfc.downloadFailed'));
      const blob = await res.blob();
      // Prefer the server-provided filename (QR requests return a .zip = xlsx + QR images).
      let name = `${requestId}.xlsx`;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/i);
      if (m) name = m[1];
      else if ((blob.type || '').includes('zip')) name = `${requestId}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { flash(err.message, false); }
  };

  const downloadTextFile = (name, text) => {
    const blob = new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const downloadImportTemplate = () => {
    if (isReplacement) {
      downloadTextFile('nfc_replacement_template.csv', 'OLD_TOKEN,REASON,NOTES\nhnx.la/EXAMPLE0000000000,NFC chip defective,\n');
    } else {
      downloadTextFile('nfc_import_template.csv', 'PROJECT,BATCH,QTY,CODE,STYLE,COLOR,VENDOR\nExample Project,Batch A,100,10001555,Chair One,Black,Metric\n');
    }
  };
  // Read a chosen CSV into the import textarea (strips a leading header row if present).
  const handleImportCsv = (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let text = String(reader.result || '').replace(/^﻿/, '').trim();
      const lines = text.split(/\r?\n/);
      const first = (lines[0] || '').toUpperCase();
      if (/PROJECT|OLD_TOKEN/.test(first) && /BATCH|REASON|QTY|NOTES/.test(first)) lines.shift();
      text = lines.join('\n');
      if (isReplacement) {
        setField({ importItemsText: text });
      } else {
        applyImportRows(parseImportRows(text));
      }
      setImportFileName(file.name);
      setReplacementCheck(null);
      flash(t('nfc.flashLoadedFile', { name: file.name }));
    };
    reader.readAsText(file);
  };

  const activeRole = ctx.role;
  const showCoordinator = activeRole === 'coordinator';
  const showManager = activeRole === 'manager';


  // Live preview of the issuance import rows.
  const importPreview = useMemo(() => {
    if (isReplacement || !form.importItemsText.trim()) return null;
    const rows = parseImportRows(form.importItemsText);
    return { rows, totalQty: rows.reduce((s, r) => s + Number(r.qty || 0), 0), hasInvalid: rows.some((r) => !r.ok) };
  }, [form.importItemsText, isReplacement]);

  // Edit a preview cell → re-serialize rows AND re-sync the top fields from the (possibly new) first row.
  const updateImportCell = (idx, field, value) => {
    if (!importPreview) return;
    applyImportRows(importPreview.rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  // Replacement editable rows = parsed text merged with the latest ledger validation (by index).
  const replacementRows = useMemo(() => {
    if (!isReplacement) return [];
    const rows = parseReplacementRows(form.importItemsText);
    const checked = replacementCheck?.items || [];
    return rows.map((r, i) => ({ ...r, ...(checked[i] || {}) }));
  }, [isReplacement, form.importItemsText, replacementCheck]);

  const updateReplacementCell = (idx, field, value) => {
    const rows = parseReplacementRows(form.importItemsText);
    if (!rows[idx]) return;
    rows[idx] = { ...rows[idx], [field]: value };
    setField({ importItemsText: serializeReplacementRows(rows) });
  };

  // Save allowed for replacement only when every defective token validated OK (mirrors setReplacementSaveState).
  const replWarnings = replacementCheck?.warnings || [];
  const canSaveRepl = !isReplacement
    || (replacementRows.length > 0 && !validatingRepl && !!replacementCheck && replWarnings.length === 0);

  const itemSummary = (r) => {
    const n = (r.importItems && r.importItems.length) || 0;
    if (String(r.requestType).toUpperCase() === 'REPLACEMENT') return t('nfc.nReplacements', { count: n });
    return n ? t('nfc.nItems', { count: n }) : t('nfc.single');
  };
  // Distinct product Codes across the request's items. Replaces the old destination summary — Destination
  // is no longer collected (Max, 2026-08-05).
  const codeSummary = (r) => {
    const items = r.importItems && r.importItems.length ? r.importItems : [{ code: r.code }];
    const set = new Set();
    items.forEach((it) => { const v = String((it && it.code) || r.code || '').trim(); if (v) set.add(v.toUpperCase()); });
    const n = set.size;
    return t('nfc.nCodes', { count: n });
  };

  return (
    <div className="nfc-page">
      <Header />
      <main className="nfc-shell">
        <div className="nfc-app">
          <div className="nfc-topbar">
            <div className="nfc-who">
              <span className="nfc-email">{ctx.email || t('nfc.notSignedIn')}</span>
              {ctx.roles.length > 1 ? (
                <span className="nfc-role-switch">
                  <span>{t('nfc.role')}</span>
                  <select value={activeRole || ''} onChange={(e) => setCtx((p) => ({ ...p, role: e.target.value }))}>
                    {ctx.roles.map((r) => <option key={r} value={r}>{r === 'manager' ? t('nfc.manager') : t('nfc.coordinator')}</option>)}
                  </select>
                </span>
              ) : <span className="nfc-role-pill">{activeRole ? (activeRole === 'manager' ? t('nfc.manager') : t('nfc.coordinator')) : t('nfc.unassigned')}</span>}
            </div>
            <ModuleHelp steps={TOUR} tourKey="nfcPortal" ready={!loading} />
          </div>

          {(message || error) && <div className={`nfc-alert ${error ? 'error' : 'success'}`}>{error || message}</div>}

          {loading ? <div className="nfc-card">{t('nfc.loading')}</div> : !ctx.roles.length ? (
            <div className="nfc-card">
              <h2>{t('nfc.noAccessTitle')}</h2>
              <p>{t('nfc.accountWord')} <b>{ctx.email}</b> {t('nfc.noAccessBody')}</p>
            </div>
          ) : (
            <>
              {showCoordinator && (
                <>
                  {canSection('nfc_create_request') && (
                  <div className="nfc-card">
                    <h2>{t('nfc.createTitle')}</h2>
                    <div className="nfc-type-toggle">
                      <button type="button" className={!isReplacement ? 'active' : ''} onClick={() => { setField({ requestType: 'ISSUANCE' }); setReplacementCheck(null); }}>{t('nfc.tabIssuance')}</button>
                      <button type="button" className={isReplacement ? 'active' : ''} onClick={() => { setField({ requestType: 'REPLACEMENT' }); setReplacementCheck(null); }}>{t('nfc.tabReplacement')}</button>
                    </div>

                    {!isReplacement ? (
                      <>
                        <div className="nfc-row">
                          <label>{t('nfc.labelType')}
                            <select value={form.labelType} onChange={(e) => setField({ labelType: e.target.value })}>
                              <option value="NFC">{t('nfc.labelNfc')}</option>
                              <option value="QR">{t('nfc.labelQr')}</option>
                            </select>
                          </label>
                        </div>
                        {/* Manual single-item fields hide once a multi-item import is loaded (ports setManualFieldsVisible). */}
                        {!(importPreview && importPreview.rows.length) && (
                          <>
                            <div className="nfc-row two">
                              <label>{t('nfc.projectName')} *<input value={form.projectName} onChange={(e) => setField({ projectName: e.target.value })} /></label>
                              <label>{t('nfc.batchName')} *<input value={form.batchName} onChange={(e) => setField({ batchName: e.target.value })} /></label>
                            </div>
                            <div className="nfc-row two">
                              <label>{t('nfc.qty')}<input type="number" min="1" max="50000" value={form.qty} onChange={(e) => setField({ qty: e.target.value })} placeholder={t('nfc.qtyPh')} /></label>
                              <label>{t('nfc.style')}<input value={form.style} onChange={(e) => setField({ style: e.target.value })} placeholder={t('nfc.stylePh')} /></label>
                            </div>
                            <div className="nfc-row two">
                              <label>{t('nfc.color')}<input value={form.color} onChange={(e) => setField({ color: e.target.value })} /></label>
                              <label>{t('nfc.vendor')}<input value={form.vendor} onChange={(e) => setField({ vendor: e.target.value })} placeholder={t('nfc.vendorPh')} /></label>
                            </div>
                            <div className="nfc-row">
                              <label>{t('nfc.code')}<input value={form.code} onChange={(e) => setField({ code: e.target.value })} placeholder={t('nfc.codePh')} /></label>
                            </div>
                          </>
                        )}
                        <div className="nfc-import-box">
                          <div className="nfc-import-box-label">{t('nfc.importTitle')}</div>
                          <div className="nfc-import-actions">
                            <button type="button" className="nfc-btn ghost" onClick={downloadImportTemplate}>{t('nfc.downloadTemplate')}</button>
                            <label className="nfc-file-picker"><span>{t('nfc.chooseCsv')}</span><input type="file" accept=".csv,text/csv" onChange={(e) => { handleImportCsv(e.target.files); e.target.value = ''; }} /></label>
                            <span className="nfc-file-name">{importFileName || t('nfc.noFile')}</span>
                          </div>
                          {importPreview && importPreview.rows.length > 0 ? (
                            <div className="nfc-import-preview">
                              <div className="nfc-import-summary">{t('nfc.importSummary', { count: importPreview.rows.length, qty: importPreview.totalQty })}{importPreview.hasInvalid && <span className="nfc-preview-warn"> {t('nfc.importInvalid')}</span>}</div>
                              <div className="nfc-table-scroll">
                                <table className="nfc-import-edit-table">
                                  <thead><tr><th>{t('nfc.projectName')}</th><th>{t('nfc.batchName')}</th><th>{t('nfc.qty')}</th><th>{t('nfc.code')}</th><th>{t('nfc.style')}</th><th>{t('nfc.color')}</th><th>{t('nfc.vendor')}</th></tr></thead>
                                  <tbody>
                                    {importPreview.rows.map((r, idx) => (
                                      <tr key={idx} className={r.ok ? '' : 'nfc-row-invalid'}>
                                        <td><input value={r.projectName} onChange={(e) => updateImportCell(idx, 'projectName', e.target.value)} /></td>
                                        <td><input value={r.batchName} onChange={(e) => updateImportCell(idx, 'batchName', e.target.value)} /></td>
                                        <td><input type="number" min="1" value={r.qty} onChange={(e) => updateImportCell(idx, 'qty', e.target.value)} /></td>
                                        <td><input value={r.code} onChange={(e) => updateImportCell(idx, 'code', e.target.value)} /></td>
                                        <td><input value={r.style} onChange={(e) => updateImportCell(idx, 'style', e.target.value)} /></td>
                                        <td><input value={r.color} onChange={(e) => updateImportCell(idx, 'color', e.target.value)} /></td>
                                        <td><input value={r.vendor} onChange={(e) => updateImportCell(idx, 'vendor', e.target.value)} /></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : <div className="nfc-import-empty">{t('nfc.importEmpty')}</div>}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="nfc-row">
                          <label>{t('nfc.labelType')}
                            <select value={form.labelType} onChange={(e) => setField({ labelType: e.target.value })}>
                              <option value="NFC">{t('nfc.labelNfc')}</option>
                              <option value="QR">{t('nfc.labelQr')}</option>
                            </select>
                          </label>
                        </div>
                        <div className="nfc-import-box">
                          <div className="nfc-import-box-label">{t('nfc.importReplTitle')}</div>
                          <div className="nfc-import-actions">
                            <button type="button" className="nfc-btn ghost" onClick={downloadImportTemplate}>{t('nfc.downloadTemplate')}</button>
                            <label className="nfc-file-picker"><span>{t('nfc.chooseCsv')}</span><input type="file" accept=".csv,text/csv" onChange={(e) => { handleImportCsv(e.target.files); e.target.value = ''; }} /></label>
                            <span className="nfc-file-name">{importFileName || t('nfc.noFile')}</span>
                          </div>
                          {replacementRows.length > 0 ? (
                            <div className="nfc-import-preview">
                              <div className="nfc-import-summary">{t('nfc.replSummary', { count: replacementRows.length })}{validatingRepl && <span className="nfc-preview-warn"> {t('nfc.replChecking')}</span>}</div>
                              {replWarnings.length > 0 && (
                                <div className="nfc-import-warning">{t('nfc.warning')} {replWarnings.map((w, i) => <React.Fragment key={i}>{i > 0 && <br />}{w}</React.Fragment>)}</div>
                              )}
                              <div className="nfc-table-scroll">
                                <table className="nfc-import-edit-table">
                                  <thead><tr><th>{t('nfc.oldToken')}</th><th>{t('nfc.reason')}</th><th>{t('nfc.notes')}</th><th>{t('nfc.status')}</th></tr></thead>
                                  <tbody>
                                    {replacementRows.map((r, idx) => {
                                      const bad = r.validationStatus && r.validationStatus !== 'OK';
                                      return (
                                        <tr key={idx} className={bad ? 'nfc-row-invalid' : ''}>
                                          <td><input value={r.oldToken} onChange={(e) => updateReplacementCell(idx, 'oldToken', e.target.value)} /></td>
                                          <td><input value={r.reason} onChange={(e) => updateReplacementCell(idx, 'reason', e.target.value)} /></td>
                                          <td><input value={r.notes} onChange={(e) => updateReplacementCell(idx, 'notes', e.target.value)} /></td>
                                          <td className={`nfc-repl-status ${bad ? 'bad' : (r.validationStatus === 'OK' ? 'ok' : '')}`} title={r.validationMessage || ''}>{REPL_STATUS_LABEL[r.validationStatus] ? t(REPL_STATUS_LABEL[r.validationStatus]) : (validatingRepl ? t('nfc.checking') : t('nfc.notChecked'))}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : <div className="nfc-import-empty">{t('nfc.replEmpty')}</div>}
                        </div>
                      </>
                    )}

                    <label className="nfc-import-label">{t('nfc.notes')}</label>
                    <textarea className="nfc-import" rows={2} value={form.notes} onChange={(e) => setField({ notes: e.target.value })} placeholder={t('nfc.optional')} />

                    <div className="nfc-form-actions">
                      <button type="button" className="nfc-btn primary" disabled={saving || !canSaveRepl || !canEditSection('nfc_create_request')} onClick={() => handleSave(false)}>{saving ? t('nfc.saving') : t('nfc.save')}</button>
                      <button type="button" className="nfc-btn ghost" onClick={cancelForm}>{t('nfc.cancel')}</button>
                    </div>
                  </div>
                  )}

                  <div className="nfc-card" data-tour="nfc-mine">
                    <h2>{t('nfc.myRequests')} <button type="button" className="nfc-btn ghost sm" onClick={load}>{t('nfc.reload')}</button></h2>
                    <div className="nfc-table-scroll">
                      <table className="nfc-table">
                        <thead><tr><th>{t('nfc.colId')}</th><th className="nfc-col-project">{t('nfc.colProject')}</th><th className="nfc-col-batch">{t('nfc.colBatch')}</th><th>{t('nfc.colQty')}</th><th>{t('nfc.colItems')}</th><th>{t('nfc.colLabel')}</th><th>{t('nfc.colVendor')}</th><th>{t('nfc.colCode')}</th><th>{t('nfc.colStatus')}</th><th>{t('nfc.colUpdated')}</th><th>{t('nfc.colFile')}</th><th>{t('nfc.colAction')}</th></tr></thead>
                        <tbody>
                          {myRequests.map((r) => {
                            const st = String(r.status).toUpperCase();
                            const editable = st === 'DRAFT' || st === 'REJECTED';
                            return (
                              <tr key={r.requestId}>
                                <td className="mono">{r.requestId}</td>
                                <td>{r.projectName || '—'}</td>
                                <td>{r.batchName || '—'}</td>
                                <td className="num">{r.qty}</td>
                                <td>{itemSummary(r)}</td>
                                <td>{r.labelType}</td>
                                <td>{r.vendor || '—'}</td>
                                <td>{codeSummary(r)}</td>
                                <td><StatusBadge status={r.status} /></td>
                                <td className="nowrap">{fmtDate(r.requestedAt)}</td>
                                <td>{st === 'APPROVED' ? <button type="button" className="nfc-icon" title={t('nfc.downloadOrder')} onClick={() => downloadFile(r.requestId)}><Download size={15} /></button> : '—'}</td>
                                <td className="nfc-actions">
                                  <button type="button" className="nfc-icon" title={t('nfc.viewDetail')} onClick={() => setDetail(r)}><Eye size={15} /></button>
                                  {editable && <button type="button" className="nfc-icon" title={t('nfc.edit')} onClick={() => editRequest(r)}><Pencil size={15} /></button>}
                                  {editable && <button type="button" className="nfc-icon green" title={t('nfc.submitApproval')} onClick={() => submitExisting(r.requestId)}><Send size={15} /></button>}
                                  {editable && <button type="button" className="nfc-icon red" title={t('nfc.delete')} onClick={() => setDeleteFor(r.requestId)}><Trash2 size={15} /></button>}
                                </td>
                              </tr>
                            );
                          })}
                          {!myRequests.length && <tr><td colSpan="12" className="nfc-empty">{t('nfc.noRequests')}</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {showManager && (
                <>
                <div className="nfc-card" data-tour="nfc-review">
                  <h2>{t('nfc.reviewTitle')} <button type="button" className="nfc-btn ghost sm" onClick={load}>{t('nfc.reload')}</button></h2>
                  <div className="nfc-table-scroll">
                    <table className="nfc-table">
                      <thead><tr><th>{t('nfc.colId')}</th><th className="nfc-col-project">{t('nfc.colProject')}</th><th className="nfc-col-batch">{t('nfc.colBatch')}</th><th>{t('nfc.colQty')}</th><th>{t('nfc.colItems')}</th><th>{t('nfc.colLabel')}</th><th>{t('nfc.colVendor')}</th><th>{t('nfc.colCode')}</th><th>{t('nfc.colOld')}</th><th>{t('nfc.colNew')}</th><th>{t('nfc.colOutput')}</th><th>{t('nfc.colRequestedBy')}</th><th>{t('nfc.colAction')}</th></tr></thead>
                      <tbody>
                        {managerRequests.map((r) => {
                          const st = String(r.status).toUpperCase();
                          const canDecide = st === 'SUBMITTED' || st === 'PENDING';
                          return (
                            <tr key={r.requestId}>
                              <td className="mono">{r.requestId}</td>
                              <td>{r.projectName || '—'}</td>
                              <td>{r.batchName || '—'}</td>
                              <td className="num">{r.qty}</td>
                              <td>{itemSummary(r)}</td>
                              <td>{r.labelType}</td>
                              <td>{r.vendor || '—'}</td>
                              <td>{codeSummary(r)}</td>
                              <td>{r.previousStatus ? <StatusBadge status={r.previousStatus} /> : '—'}</td>
                              <td><StatusBadge status={r.status} /></td>
                              <td>{st === 'APPROVED' ? <button type="button" className="nfc-icon" title={t('nfc.downloadOrder')} onClick={() => downloadFile(r.requestId)}><Download size={15} /></button> : '—'}</td>
                              <td className="nowrap">{r.requestedBy}<br />{fmtDate(r.requestedAt)}</td>
                              <td className="nfc-actions">
                                <button type="button" className="nfc-icon" title={t('nfc.viewDetail')} onClick={() => setDetail(r)}><Eye size={15} /></button>
                                {canDecide && canEditSection('nfc_request_review') && <button type="button" className="nfc-icon green" title={t('xNfc.approve')} disabled={!!(approveProg && approveProg.status === 'running')} onClick={() => approve(r.requestId)}><Check size={15} /></button>}
                                {canDecide && canEditSection('nfc_request_review') && <button type="button" className="nfc-icon red" title={t('nfc.reject')} onClick={() => { setRejectFor(r.requestId); setRejectReason(''); setRejectError(false); }}><X size={15} /></button>}
                              </td>
                            </tr>
                          );
                        })}
                        {!managerRequests.length && <tr><td colSpan="13" className="nfc-empty">{t('nfc.noRequests')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                {canSection('nfc_manage_access') && (
                <div className="nfc-card">
                  <h2>{t('nfc.accessTitle')} <button type="button" className="nfc-btn ghost sm" onClick={loadAccess}>{t('nfc.reload')}</button></h2>
                  <p className="nfc-access-hint">{t('nfc.accessHint')}</p>
                  <div className="nfc-access-add">
                    <input
                      list="nfc-known-users" type="email" placeholder="user@helinox.com"
                      value={newAccessEmail} onChange={(e) => setNewAccessEmail(e.target.value)}
                    />
                    <datalist id="nfc-known-users">{knownUsers.map((u) => <option key={u} value={u} />)}</datalist>
                    <select value={newAccessRole} onChange={(e) => setNewAccessRole(e.target.value)}>
                      <option value="coordinator">{t('nfc.coordinator')}</option>
                      <option value="manager">{t('nfc.manager')}</option>
                    </select>
                    <button type="button" className="nfc-btn primary" disabled={accessBusy || !canEditSection('nfc_manage_access')} onClick={addAccess}>{t('nfc.grantAccess')}</button>
                  </div>
                  <div className="nfc-table-scroll">
                    <table className="nfc-table">
                      <thead><tr><th className="nfc-col-project">{t('nfc.colEmail')}</th><th>{t('nfc.coordinator')}</th><th>{t('nfc.manager')}</th><th>{t('nfc.colAddedBy')}</th></tr></thead>
                      <tbody>
                        {accessList.map((a) => (
                          <tr key={a.email}>
                            <td className="mono">{a.email}</td>
                            <td><input type="checkbox" disabled={accessBusy || !canEditSection('nfc_manage_access')} checked={a.roles.includes('coordinator')} onChange={(e) => toggleAccess(a.email, 'coordinator', e.target.checked)} /></td>
                            <td><input type="checkbox" disabled={accessBusy || !canEditSection('nfc_manage_access')} checked={a.roles.includes('manager')} onChange={(e) => toggleAccess(a.email, 'manager', e.target.checked)} /></td>
                            <td>{a.addedBy || '—'}</td>
                          </tr>
                        ))}
                        {!accessList.length && <tr><td colSpan="4" className="nfc-empty">{t('nfc.noUsers')}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {detail && (
        <div className="nfc-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setDetail(null); }}>
          <div className="nfc-modal">
            <div className="nfc-modal-head"><h3>{t('nfc.detailTitle')} — {detail.requestId}</h3><button type="button" onClick={() => setDetail(null)}>&times;</button></div>
            <div className="nfc-modal-body">
              <div className="nfc-detail-field">
                <span className="lbl">{t('nfc.lblNotes')}</span>
                <span className="val">{detail.notes || '—'}</span>
              </div>
              <div className="nfc-detail-field">
                <span className="lbl">{t('nfc.lblOutputFile')}</span>
                <span className="val">
                  {String(detail.status).toUpperCase() === 'APPROVED'
                    ? <button type="button" className="nfc-linklike" onClick={() => downloadFile(detail.requestId)}>{t('nfc.openOutput')}</button>
                    : '—'}
                </span>
              </div>
              {detail.decisionNote ? (
                <div className="nfc-detail-field"><span className="lbl">{t('nfc.lblDecisionNote')}</span><span className="val">{detail.decisionNote}</span></div>
              ) : null}
              {detail.importItems && detail.importItems.length > 0 && (
                <div className="nfc-detail-items">
                  <h4>{String(detail.requestType).toUpperCase() === 'REPLACEMENT' ? t('nfc.replItems') : t('nfc.items')}</h4>
                  <div className="nfc-table-scroll">
                    {String(detail.requestType).toUpperCase() === 'REPLACEMENT' ? (
                      <table className="nfc-import-edit-table">
                        <thead><tr><th>{t('nfc.oldToken')}</th><th>{t('nfc.colProject')}</th><th>{t('nfc.colBatch')}</th><th>{t('nfc.style')}</th><th>{t('nfc.color')}</th><th>{t('nfc.vendor')}</th><th>{t('nfc.reason')}</th><th>{t('nfc.notes')}</th></tr></thead>
                        <tbody>
                          {detail.importItems.map((it, i) => (
                            <tr key={i}>
                              <td className="mono">{it.oldNfcUrl || it.oldToken || ''}</td>
                              <td>{it.projectName || ''}</td><td>{it.batchName || ''}</td><td>{it.style || ''}</td>
                              <td>{it.color || ''}</td><td>{it.vendor || ''}</td><td>{it.reason || ''}</td><td>{it.notes || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <table className="nfc-import-edit-table">
                        <thead><tr><th>{t('nfc.colProject')}</th><th>{t('nfc.colBatch')}</th><th>{t('nfc.qty')}</th><th>{t('nfc.code')}</th><th>{t('nfc.style')}</th><th>{t('nfc.color')}</th><th>{t('nfc.vendor')}</th></tr></thead>
                        <tbody>
                          {detail.importItems.map((it, i) => (
                            <tr key={i}>
                              <td>{it.projectName || detail.projectName || ''}</td><td>{it.batchName || detail.batchName || ''}</td>
                              <td className="num">{it.qty || ''}</td>
                              <td>{it.code || detail.code || ''}</td>
                              <td>{it.style || ''}</td><td>{it.color || ''}</td>
                              <td>{it.vendor || detail.vendor || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="nfc-modal-foot">
              <button type="button" className="nfc-btn ghost" onClick={() => setDetail(null)}>{t('nfc.close')}</button>
            </div>
          </div>
        </div>
      )}

      {approveProg && (
        <div className="nfc-modal-overlay">
          <div className="nfc-modal sm">
            <div className="nfc-modal-head"><h3>{t('nfc.genTitle')}</h3></div>
            <div className="nfc-modal-body">
              <p className="nfc-prog-lead">{t('nfc.requestWord')} <b>{approveProg.requestId}</b> {t('nfc.genLead')} <b>{t('nfc.genLeadBold')}</b>.</p>
              <div className="nfc-prog-meta">
                <span>{approveProg.phase || t('nfc.working')}</span>
                <strong>{approveProg.total ? `${approveProg.done || 0} / ${approveProg.total}` : '...'}</strong>
              </div>
              <div className="nfc-prog-bar">
                <div
                  className={`nfc-prog-fill${approveProg.status === 'error' ? ' err' : ''}${approveProg.total ? '' : ' indet'}`}
                  style={approveProg.total ? { width: `${Math.min(100, Math.round(((approveProg.done || 0) / approveProg.total) * 100))}%` } : undefined}
                />
              </div>
              <p className={`nfc-prog-msg${approveProg.status === 'error' ? ' err' : ''}`}>{approveProg.message}</p>
            </div>
            <div className="nfc-modal-foot">
              <button type="button" className="nfc-btn ghost" disabled={approveProg.status === 'running'} onClick={() => { stopApprovePoll(); setApproveProg(null); }}>{t('nfc.close')}</button>
            </div>
          </div>
        </div>
      )}

      {rejectFor && (
        <div className="nfc-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setRejectFor(null); }}>
          <div className="nfc-modal sm">
            <div className="nfc-modal-head"><h3>{t('nfc.rejectTitle')} {rejectFor}</h3><button type="button" onClick={() => setRejectFor(null)}>&times;</button></div>
            <div className="nfc-modal-body">
              <label className="nfc-import-label">{t('nfc.rejectReason')}</label>
              <textarea className="nfc-import" rows={3} value={rejectReason} onChange={(e) => { setRejectReason(e.target.value); if (rejectError) setRejectError(false); }} placeholder={t('nfc.rejectReasonPh')} />
              {rejectError && <div className="nfc-field-error">{t('nfc.rejectReasonReq')}</div>}
            </div>
            <div className="nfc-modal-foot">
              <button type="button" className="nfc-btn red" onClick={doReject}>{t('nfc.rejectBtn')}</button>
              <button type="button" className="nfc-btn ghost" onClick={() => setRejectFor(null)}>{t('nfc.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {deleteFor && (
        <div className="nfc-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setDeleteFor(null); }}>
          <div className="nfc-modal sm">
            <div className="nfc-modal-head"><h3>{t('nfc.deleteTitle')}</h3><button type="button" onClick={() => setDeleteFor(null)}>&times;</button></div>
            <div className="nfc-modal-body">
              <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>{t('nfc.deleteBody', { id: deleteFor })}</p>
            </div>
            <div className="nfc-modal-foot">
              <button type="button" className="nfc-btn red" onClick={doDeleteRequest}>{t('nfc.delete')}</button>
              <button type="button" className="nfc-btn ghost" onClick={() => setDeleteFor(null)}>{t('nfc.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NFCPortal;
